import { UserManager, WebStorageStateStore, type User } from "oidc-client-ts";

export interface AuthConfig {
  enabled: boolean;
  authority?: string;
  audience?: string;
  discovery?: string;
}

let cached: AuthConfig | null = null;
let manager: UserManager | null = null;
let currentUser: User | null = null;
const listeners = new Set<(u: User | null) => void>();

export async function fetchAuthConfig(): Promise<AuthConfig> {
  if (cached) return cached;
  const res = await fetch("/api/auth/config", { credentials: "include" });
  if (!res.ok) {
    cached = { enabled: false };
    return cached;
  }
  cached = (await res.json()) as AuthConfig;
  return cached;
}

function buildManager(cfg: AuthConfig): UserManager {
  if (!cfg.authority || !cfg.audience) throw new Error("auth_config_incomplete");
  return new UserManager({
    authority: cfg.authority,
    client_id: cfg.audience,
    redirect_uri: window.location.origin + "/auth/callback",
    post_logout_redirect_uri: window.location.origin + "/",
    response_type: "code",
    scope: "openid profile email",
    userStore: new WebStorageStateStore({ store: window.localStorage }),
    automaticSilentRenew: true,
  });
}

export async function initAuth(): Promise<{ enabled: boolean; user: User | null }> {
  const cfg = await fetchAuthConfig();
  if (!cfg.enabled) return { enabled: false, user: null };
  manager = buildManager(cfg);
  manager.events.addUserLoaded((u) => {
    currentUser = u;
    listeners.forEach((cb) => cb(u));
  });
  manager.events.addUserUnloaded(() => {
    currentUser = null;
    listeners.forEach((cb) => cb(null));
  });
  manager.events.addAccessTokenExpired(() => {
    currentUser = null;
    listeners.forEach((cb) => cb(null));
  });

  const u = await manager.getUser();
  if (u && !u.expired) {
    currentUser = u;
    return { enabled: true, user: u };
  }
  return { enabled: true, user: null };
}

export async function handleCallback(): Promise<User | null> {
  if (!manager) throw new Error("auth_not_initialized");
  const u = await manager.signinRedirectCallback();
  currentUser = u;
  listeners.forEach((cb) => cb(u));
  return u;
}

export async function login(state?: unknown): Promise<void> {
  if (!manager) throw new Error("auth_not_initialized");
  await manager.signinRedirect({ state: state as Record<string, unknown> | undefined });
}

export async function logout(): Promise<void> {
  if (!manager) throw new Error("auth_not_initialized");
  await manager.signoutRedirect();
}

export function getAccessToken(): string | null {
  return currentUser?.access_token ?? null;
}

export function getCurrentUser(): User | null {
  return currentUser;
}

export function subscribe(cb: (u: User | null) => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}
